import { useNavigate } from 'react-router';
import { useModuleContext } from '../context/ModuleContext';
import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { fetchFocusInsights, fetchCustomers, fetchLocations, fetchSites } from '../services/focus360Service';
import { ChevronDown } from 'lucide-react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { LoaderCircle } from 'lucide-react';
import React from 'react';
import { getUserIdFromToken } from '../utils/authUtils';
import { useDebounce } from '../hooks/useDebounce';
import { useChatHeadersContext } from '../context/ChatHeadersContext';

// The customers API returns nothing below this length; mirrors its min_chars.
const MIN_CUSTOMER_SEARCH_CHARS = 2;

export default function Focus360Component({ onMessageAdd }) {
    const navigate = useNavigate();
    const { getCurrentApplicationId } = useModuleContext();
    const { updateChatHeader } = useChatHeadersContext();
    // State for selected values
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedSites, setSelectedSites] = useState([]);

    // State for search queries
    const [customerQuery, setCustomerQuery] = useState('');
    const [locationQuery, setLocationQuery] = useState('');

    // State for API data
    const [customers, setCustomers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [sites, setSites] = useState([]);

    // State for loading states
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [isLoadingSites, setIsLoadingSites] = useState(false);
    const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);

    // State for dropdown open states
    const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
    const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);

    // Debounced customer query
    const debouncedCustomerQuery = useDebounce(customerQuery, 500);

    // Fetch customers with debouncing
    useEffect(() => {
        const loadCustomers = async () => {
            // Below the minimum the API returns no rows anyway, so skip the round trip.
            if (debouncedCustomerQuery.trim().length < MIN_CUSTOMER_SEARCH_CHARS) {
                setCustomers([]);
                return;
            }

            setIsLoadingCustomers(true);
            try {
                const response = await fetchCustomers(debouncedCustomerQuery);
                // Only store customers with valid data
                const validCustomers = (response.data || []).filter(customer => 
                    customer && 
                    customer.group_key && 
                    customer.group_account_name &&
                    customer.group_key.trim() !== '' && 
                    customer.group_account_name.trim() !== ''
                );
                setCustomers(validCustomers);
            } catch (error) {
                console.error('Error fetching customers:', error);
                setCustomers([]);
            } finally {
                setIsLoadingCustomers(false);
            }
        };

        loadCustomers();
    }, [debouncedCustomerQuery]);

    // Load initial customers on component mount
    useEffect(() => {
        const loadInitialCustomers = async () => {
            setIsLoadingCustomers(true);
            try {
                const response = await fetchCustomers('');
                // Only store customers with valid data
                const validCustomers = (response.data || []).filter(customer => 
                    customer && 
                    customer.group_key && 
                    customer.group_account_name &&
                    customer.group_key.trim() !== '' && 
                    customer.group_account_name.trim() !== ''
                );
                setCustomers(validCustomers);
            } catch (error) {
                console.error('Error fetching initial customers:', error);
                setCustomers([]);
            } finally {
                setIsLoadingCustomers(false);
            }
        };

        loadInitialCustomers();
    }, []);

    // Fetch locations when customer is selected and location dropdown opens
    const loadLocations = useCallback(async () => {
        if (!selectedCustomer || isLoadingLocations) return;

        setIsLoadingLocations(true);
        try {
            const response = await fetchLocations(selectedCustomer.group_key);
            // Only store locations with valid data
            const validLocations = (response || []).filter(location => 
                location && 
                location.country && 
                location.country_key && 
                location.country.trim() !== '' && 
                location.country_key.trim() !== ''
            );
            setLocations(validLocations);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        } finally {
            setIsLoadingLocations(false);
        }
    }, [selectedCustomer, isLoadingLocations]);

    // Fetch sites when location is selected and site dropdown opens
    const loadSites = useCallback(async () => {
        if (!selectedCustomer || !selectedLocation || isLoadingSites) return;

        setIsLoadingSites(true);
        try {
            const response = await fetchSites(selectedCustomer.group_key, selectedLocation.country_key);
            // Only store sites with valid data
            const validSites = (response || []).filter(site => 
                site && 
                site.site_uid && 
                site.site_account_name &&
                site.site_uid.trim() !== '' && 
                site.site_account_name.trim() !== ''
            );
            setSites(validSites);
        } catch (error) {
            console.error('Error fetching sites:', error);
            setSites([]);
        } finally {
            setIsLoadingSites(false);
        }
    }, [selectedCustomer, selectedLocation, isLoadingSites]);

    // Handle location dropdown open
    const handleLocationDropdownOpen = () => {
        setIsLocationDropdownOpen(true);
        if (selectedCustomer && locations.length === 0) {
            loadLocations();
        }
    };

    // Handle site dropdown open
    const handleSiteDropdownOpen = () => {
        setIsSiteDropdownOpen(true);
        if (selectedCustomer && selectedLocation && sites.length === 0) {
            loadSites();
        }
    };

    // Customers are searched, ranked and limited by the API, which also matches on
    // site names; a client-side filter on the group name would drop those matches.
    const customerQueryTooShort =
        customerQuery.trim().length > 0 && customerQuery.trim().length < MIN_CUSTOMER_SEARCH_CHARS;

    const filteredLocations = locations.filter((l) =>
        l && l.country && l.country.toLowerCase().includes(locationQuery.toLowerCase())
    );

    // Handle customer change
    const handleCustomerChange = (customer) => {
        setSelectedCustomer(customer);
        setSelectedLocation(null);
        setSelectedSites([]);
        setLocations([]);
        setSites([]);
        setLocationQuery('');
    };

    // Handle location change
    const handleLocationChange = (location) => {
        setSelectedLocation(location);
        setSelectedSites([]);
        setSites([]);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsGeneratingInsights(true);
        const chatId = uuidv4();
        const applicationId = getCurrentApplicationId();
        const userId = getUserIdFromToken();

        if (!userId) {
            console.log('user id is not defined in token');
            return;
        }

        const query = '';
        // Validate selected data before submission
        if (!selectedCustomer?.group_key || !selectedLocation?.country_key) {
            console.error('Invalid customer or location data');
            setIsGeneratingInsights(false);
            return;
        }

        const payload = {
            chat_id: chatId,
            query,
            customer_id: selectedCustomer.group_key,
            location: selectedLocation.country_key,
            country_code: selectedLocation.country_key,
            country: selectedLocation.country,
            site_id: selectedSites,
            is_new_session: true,
            application_id: applicationId,
            user_id: userId,
        };

        try {
            const response = await fetchFocusInsights(payload);
            const aiMessage = JSON.stringify(response, null, 2);
            const reportSiteInfo = Array.isArray(selectedSites)
                ? selectedSites
                    .map((siteUid) => sites.find((site) => site.site_uid === siteUid))
                    .filter(Boolean)
                    .map((site) => ({
                        SiteUID: site.site_uid,
                        SiteAccount_Name: site.site_account_name,
                        Address: site.address,
                        Country: selectedLocation?.country || '',
                        City: site.city,
                    }))
                : [];

            let headerText = response.header || response.data?.header;
            if (!headerText && selectedCustomer && selectedLocation) {
                headerText = `Focus360 Insights - ${selectedCustomer.group_account_name} (${selectedLocation.country})`;
            }
            
            if (headerText) {
                updateChatHeader(chatId, headerText);
            }

            onMessageAdd({ role: 'user', content: '' });
            onMessageAdd({
                role: 'assistant',
                content: aiMessage,
                reportCustomerName: selectedCustomer?.group_account_name || '',
                reportLocation: selectedLocation?.country || '',
                reportSiteInfo,
            });

            if (response.status === 'success') {
                navigate(`/chat/${chatId}`);
            }
        } catch (error) {
            console.error('Error generating insights:', error);
        } finally {
            setIsGeneratingInsights(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-auto flex flex-col p-4">
            <div className="flex gap-4 flex-col lg:flex-row">
                {/* Customer Combobox */}
                <div className="min-w-40">
                    <label className="block text-sm text-white mb-1">Customer <span className="text-xs text-red-500">*</span></label>
                    <Combobox value={selectedCustomer} onChange={handleCustomerChange}>
                        <div className="relative">
                            <ComboboxInput
                                className="w-full p-2 rounded bg-white/10 text-white"
                                displayValue={(customer) => customer?.group_account_name || ''}
                                onChange={(event) => setCustomerQuery(event.target.value)}
                                placeholder="Search Customer"
                                onClick={() => setCustomerQuery(customerQuery)} // Trigger dropdown open on click
                            />
                            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                                {isLoadingCustomers ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                ) : (
                                    <ChevronDown size={16} />
                                )}
                            </ComboboxButton>
                            <ComboboxOptions className="absolute z-50 bg-black/50 mt-2 rounded shadow-lg max-h-60 overflow-auto backdrop-blur w-full">
                                {isLoadingCustomers ? (
                                    <div className="p-2 text-white">Loading...</div>
                                ) : customerQueryTooShort ? (
                                    <div className="p-2 text-white/60">
                                        Type at least {MIN_CUSTOMER_SEARCH_CHARS} characters
                                    </div>
                                ) : customers.length === 0 ? (
                                    <div className="p-2 text-white">No results found</div>
                                ) : (
                                    customers.map((customer) => (
                                        <ComboboxOption
                                            key={customer.group_key}
                                            value={customer}
                                            className={({ active }) =>
                                                `cursor-pointer p-2 ${active ? 'bg-white/20 text-white' : ''}`
                                            }
                                        >
                                            <div className="text-white">{customer.group_account_name}</div>
                                            {customer.matched_site_name && (
                                                <div className="text-xs text-white/60 truncate">
                                                    {customer.matched_site_name}
                                                </div>
                                            )}
                                        </ComboboxOption>
                                    ))
                                )}
                            </ComboboxOptions>
                        </div>
                    </Combobox>
                </div>

                {/* Location Combobox */}
                <div className="min-w-40">
                    <label className="block text-sm text-white mb-1">Location <span className="text-xs text-red-500">*</span></label>
                    <Combobox 
                        value={selectedLocation} 
                        onChange={handleLocationChange}
                        disabled={!selectedCustomer}
                    >
                        <div className="relative">
                            <ComboboxInput
                                className="w-full p-2 rounded bg-white/10 text-white disabled:opacity-50"
                                displayValue={(location) => location?.country || ''}
                                onChange={(event) => setLocationQuery(event.target.value)}
                                placeholder={selectedCustomer ? "Search Location" : "Select Customer First"}
                                onFocus={handleLocationDropdownOpen}
                                onClick={handleLocationDropdownOpen} // Also trigger on click
                            />
                            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                                {isLoadingLocations ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                ) : (
                                    <ChevronDown size={16} />
                                )}
                            </ComboboxButton>
                            <ComboboxOptions className="absolute z-50 bg-black/50 mt-2 rounded shadow-lg max-h-60 overflow-auto backdrop-blur w-full">
                                {isLoadingLocations ? (
                                    <div className="p-2 text-white">Loading...</div>
                                ) : filteredLocations.length === 0 ? (
                                    <div className="p-2 text-white">No results found</div>
                                ) : (
                                    filteredLocations.map((location) => (
                                        <ComboboxOption
                                            key={location.country_key}
                                            value={location}
                                            className={({ active }) =>
                                                `cursor-pointer p-2 ${active ? 'bg-white/20 text-white' : ''}`
                                            }
                                        >
                                            {location.country}
                                        </ComboboxOption>
                                    ))
                                )}
                            </ComboboxOptions>
                        </div>
                    </Combobox>
                </div>

                {/* Site Multi-Select Listbox */}
                <div className="min-w-40">
                    <label className="block text-sm text-white mb-1">Sites (Multi-select) <span className="text-xs text-red-500">*</span></label>
                    <div className="relative">
                        <Listbox 
                            value={selectedSites} 
                            onChange={setSelectedSites} 
                            multiple
                            disabled={!selectedLocation}
                        >
                            <ListboxButton 
                                className="flex justify-between items-center w-full p-2 bg-white/10 rounded text-white disabled:opacity-50"
                                onClick={handleSiteDropdownOpen}
                            >
                                <span className="truncate">
                                    {!selectedLocation ? 'Select Location First' :
                                        selectedSites.length === sites.length && sites.length > 0
                                        ? 'All Selected'
                                        : selectedSites.length === 0
                                            ? 'Select Sites'
                                            : (() => {
                                                const names = sites
                                                    .filter((s) => selectedSites.includes(s.site_uid))
                                                    .map((s) => s.site_account_name);
                                                if (names.length === 1) return names[0];
                                                if (names.length === 2) return names.join(', ');
                                                return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
                                            })()}
                                </span>
                                {isLoadingSites ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                ) : (
                                    <ChevronDown size={16} />
                                )}
                            </ListboxButton>
                            <ListboxOptions className="absolute z-50 bg-black/50 mt-2 rounded shadow-lg max-h-60 overflow-auto backdrop-blur w-full min-w-[200px]">
                                {isLoadingSites ? (
                                    <div className="p-2 text-white">Loading...</div>
                                ) : sites.length === 0 ? (
                                    <div className="p-2 text-white">No sites available</div>
                                ) : (
                                    <>
                                        <ListboxOption
                                            value="__all__"
                                            disabled={sites.length === 0}
                                            as={React.Fragment}
                                        >
                                            {({ active }) => (
                                                <div
                                                    className={`flex items-center gap-2 cursor-pointer px-2 py-1 font-bold border-b border-white/30 ${active ? 'bg-white/20 text-white' : ''
                                                        }`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (selectedSites.length === sites.length) {
                                                            setSelectedSites([]);
                                                        } else {
                                                            setSelectedSites(sites.map((site) => site.site_uid));
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedSites.length === sites.length && sites.length > 0
                                                        }
                                                        readOnly
                                                        className="accent-black"
                                                    />
                                                    <span>Select All</span>
                                                </div>
                                            )}
                                        </ListboxOption>
                                        {sites.map((site) => (
                                            <ListboxOption
                                                key={site.site_uid}
                                                value={site.site_uid}
                                                disabled={false}
                                                as={React.Fragment}
                                            >
                                                {({ active }) => (
                                                    <div
                                                        className={`flex items-center gap-2 cursor-pointer px-2 py-1 ${active ? 'bg-white/20 text-white' : ''
                                                            }`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (selectedSites.includes(site.site_uid)) {
                                                                setSelectedSites(
                                                                    selectedSites.filter((uid) => uid !== site.site_uid)
                                                                );
                                                            } else {
                                                                setSelectedSites([...selectedSites, site.site_uid]);
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSites.includes(site.site_uid)}
                                                            readOnly
                                                            className="accent-black"
                                                        />
                                                        <span>{site.site_account_name}</span>
                                                    </div>
                                                )}
                                            </ListboxOption>
                                        ))}
                                    </>
                                )}
                            </ListboxOptions>
                        </Listbox>
                    </div>
                </div>
            </div>

            <button
                className="mt-4 w-full bg-black border border-white/40 p-3 rounded-full hover:bg-white/20 duration-300 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-black"
                disabled={
                    isGeneratingInsights || 
                    !selectedCustomer?.group_key || 
                    !selectedLocation?.country_key || 
                    selectedSites.length === 0
                }
            >
                {isGeneratingInsights ? (
                    <span className="flex gap-2 justify-center">
                        <LoaderCircle className="animate-spin duration-300" /> Generating Insights...
                    </span>
                ) : (
                    <span className="flex gap-2 justify-center">Generate Insights</span>
                )}
            </button>
        </form>
    );
}
